/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { ReactNode } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { token } from '@atlaskit/tokens';

export interface PanelFooterProps {
	/**
	 * The action buttons to display in the footer (e.g. Cancel / Save buttons).
	 * Content is laid out in a row with actions aligned to the right, justified space-between.
	 */
	children?: ReactNode;
	/**
	 * Optional footer disclaimer content to display below the footer actions.
	 *
	 * Use `PanelDisclaimer` to apply the standard disclaimer styling.
	 */
	disclaimer?: ReactNode;
	/**
	 * Unique string that appears as a data attribute `data-testid` in the rendered code,
	 * often used for automated tests.
	 */
	testId?: string;
}

const styles = cssMap({
	footer: {
		borderBlockStartStyle: 'solid',
		borderBlockStartWidth: token('border.width'),
		borderColor: token('color.border'),
		backgroundColor: token('color.background.neutral.subtle'),
		display: 'flex',
		flexDirection: 'column',
	},
	footerActions: {
		paddingInlineStart: token('space.300'),
		paddingInlineEnd: token('space.300'),
		paddingBlock: token('space.200'),
		display: 'flex',
	},
	footerContent: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		flexDirection: 'row-reverse',
		width: '100%',
	},
});

/**
 * The PanelFooter component provides a footer area for panels
 * with consistent styling and spacing matching the header specifications.
 * It automatically organizes content with space-between row-reverse layout, with actions
 * starting from the right-hand side.
 *
 * Use the `disclaimer` slot with `PanelDisclaimer` to add an optional disclaimer bar
 * below the footer actions while keeping both elements inside the semantic footer.
 *
 * @example
 * <PanelContainer>
 *   <PanelFooter
 *     disclaimer={
 *       <PanelDisclaimer>
 *         Powered by Rovo AI. Verify results.
 *       </PanelDisclaimer>
 *     }
 *   >
 *     <ButtonGroup>...</ButtonGroup>
 *   </PanelFooter>
 * </PanelContainer>
 */
export function PanelFooter({ children, disclaimer, testId }: PanelFooterProps): React.JSX.Element {
	return (
		<footer data-testid={testId} css={styles.footer}>
			{children && (
				<div css={styles.footerActions}>
					<Flex xcss={styles.footerContent}>{children}</Flex>
				</div>
			)}
			{disclaimer}
		</footer>
	);
}
